// src/pages/DeviceRegistryPage/DeviceRegistryPage.tsx

import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogContent,
  Stack,
  TextField,
  Snackbar,
} from "@mui/material";
import { SearchOutlined, Close } from "@mui/icons-material";

/* ---------- Types ---------- */
interface Device {
  _id: string;
  deviceId?: string;
  connectivityStatus: "Active" | "Offline";
  batteryStatus: string;
  healthCenter: string;
  location: string;
}

interface Column {
  key: string;
  label: string;
}

const DeviceRegistryPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

  const [devices, setDevices] = useState<Device[]>([]);
  const [filters, setFilters] = useState({
    connectivityStatus: "",
    location: "",
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState({
    deviceId: "",
    healthCenter: "",
    location: "",
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [saving, setSaving] = useState(false);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "warning" | "info";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  const showMessage = (
    message: string,
    severity: "success" | "error" | "warning" | "info" = "info"
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  /* ---------- FETCH DATA ---------- */
  const fetchAndCacheData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const devRes = await fetch(`${API_BASE_URL}/api/devices`);
      if (!devRes.ok) {
        throw new Error(`HTTP ${devRes.status}: Failed to fetch devices`);
      }

      const devData = await devRes.json();
      const normalized = devData.map((d: any) => ({
        ...d,
        deviceId: d.deviceId || d._id,
      }));

      setDevices(normalized);
      sessionStorage.setItem("registry_devices", JSON.stringify(normalized));
    } catch (err: any) {
      console.error("Fetch devices error:", err);
      setError(err.message || "Failed to load devices. Using cached data if available.");

      const cached = sessionStorage.getItem("registry_devices");
      if (cached) {
        setDevices(JSON.parse(cached));
      }
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchAndCacheData();
  }, [fetchAndCacheData]);

  /* ---------- FILTER LOGIC ---------- */
  const filteredDevices = devices.filter((device) => {
    const statusMatch =
      !filters.connectivityStatus || device.connectivityStatus === filters.connectivityStatus;
    const locationMatch = !filters.location || device.location === filters.location;
    return statusMatch && locationMatch;
  });

  const uniqueLocations = Array.from(new Set(devices.map((d) => d.location))).sort();

  /* ---------- TABLE COLUMNS ---------- */
  const columns: Column[] = [
    { key: "deviceId", label: "DEVICE ID" },
    { key: "connectivityStatus", label: "CONNECTIVITY STATUS" },
    { key: "batteryStatus", label: "BATTERY STATUS" },
    { key: "healthCenter", label: "HEALTH CENTER" },
    { key: "location", label: "LOCATION" },
  ];

  /* ---------- PROCESSED TABLE DATA ---------- */
  const tableData = filteredDevices.map((d) => {
    const isActive = d.connectivityStatus === "Active";

    return {
      ...d,
      deviceId: (
        <Typography variant="body2" sx={{ color: "#FFFFFF", fontWeight: 500 }}>
          {d.deviceId}
        </Typography>
      ),
      connectivityStatus: (
        <Box
          sx={{
            px: 2,
            py: 0.5,
            borderRadius: 2,
            backgroundColor: isActive ? "#4880FF3D" : "#4B5563",
            color: isActive ? "#4880FF" : "#D1D5DB",
            fontSize: "0.75rem",
            fontWeight: 600,
            display: "inline-block",
            minWidth: 70,
            textAlign: "center",
          }}
        >
          {isActive ? "• Active" : "• Offline"}
        </Box>
      ),
      batteryStatus: (
        <Typography variant="body2" sx={{ color: "#FFFFFF", fontWeight: 600 }}>
          {d.batteryStatus}
        </Typography>
      ),
      healthCenter: (
        <Typography variant="body2" sx={{ color: "#E5E7EB" }}>
          {d.healthCenter}
        </Typography>
      ),
      location: (
        <Typography variant="body2" sx={{ color: "#E5E7EB" }}>
          {d.location}
        </Typography>
      ),
    };
  });

  /* ---------- FORM VALIDATION & SUBMIT ---------- */
  const validateForm = () => {
    const errors: { [key: string]: string } = {};
    if (!form.deviceId.trim()) errors.deviceId = "Device ID is required";
    if (!form.healthCenter.trim()) errors.healthCenter = "Health Center is required";
    if (!form.location.trim()) errors.location = "Location is required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddDevice = async () => {
    if (!validateForm()) {
      showMessage("Please fill all required fields", "warning");
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/devices`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          deviceId: form.deviceId.trim(),
          healthCenter: form.healthCenter.trim(),
          location: form.location.trim(),
          connectivityStatus: "Offline",
          batteryStatus: "0%",
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || `Failed to add device (HTTP ${res.status})`);
      }

      setOpenDialog(false);
      setForm({ deviceId: "", healthCenter: "", location: "" });
      setFormErrors({});
      await fetchAndCacheData();
      showMessage("Device added successfully", "success");
    } catch (err: any) {
      console.error("Add device error:", err);
      showMessage(err.message || "Failed to add device", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ height: "80vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        backgroundColor: "#1B2431",
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700} sx={{ color: "#FFFFFF" }}>
          Device Registry
        </Typography>

        <Button
          variant="contained"
          onClick={() => setOpenDialog(true)}
          sx={{
            backgroundColor: "#323D4E",
            color: "white",
            textTransform: "none",
            fontWeight: 600,
            "&:hover": { backgroundColor: "#3A4659" },
          }}
        >
          + Add Device
        </Button>
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Filters Bar */}
      <Box
        sx={{
          backgroundColor: "#273142",
          borderRadius: 2,
          p: 2,
          mb: 3,
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
        }}
      >
        <IconButton size="small" disabled>
          <SearchOutlined sx={{ fontSize: 18, color: "#91969E" }} />
        </IconButton>

        <Typography variant="body2" sx={{ color: "#91969E", mr: 2 }}>
          Filter By
        </Typography>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel sx={{ color: "#91969E" }}>Connectivity Status</InputLabel>
          <Select
            value={filters.connectivityStatus}
            label="Connectivity Status"
            onChange={(e) => setFilters({ ...filters, connectivityStatus: e.target.value as string })}
            sx={{
              color: "white",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#4B5563" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#6B7280" },
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Offline">Offline</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel sx={{ color: "#91969E" }}>Location</InputLabel>
          <Select
            value={filters.location}
            label="Location"
            onChange={(e) => setFilters({ ...filters, location: e.target.value as string })}
            sx={{
              color: "white",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#4B5563" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#6B7280" },
            }}
          >
            <MenuItem value="">All</MenuItem>
            {uniqueLocations.map((loc) => (
              <MenuItem key={loc} value={loc}>
                {loc}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="text"
          onClick={() => setFilters({ connectivityStatus: "", location: "" })}
          sx={{ color: "#FF8743", textTransform: "none", fontWeight: 500 }}
        >
          Clear Filters
        </Button>
      </Box>

      {/* Table */}
      <Card sx={{ borderRadius: 3, backgroundColor: "#273142", overflow: "hidden" }}>
        <CardContent sx={{ p: 0 }}>
          {/* Header Row */}
          <Box
            sx={{
              backgroundColor: "#1F2937",
              px: 3,
              py: 2,
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1.5fr 1.5fr 1.5fr 2fr 1.5fr",
              gap: 2,
              alignItems: "center",
            }}
          >
            {columns.map((col) => (
              <Typography
                key={col.key}
                variant="caption"
                sx={{ color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase" }}
              >
                {col.label}
              </Typography>
            ))}
          </Box>

          {/* Data Rows */}
          {tableData.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center", color: "#9CA3AF" }}>
              <Typography>No devices found matching the current filters.</Typography>
            </Box>
          ) : (
            tableData.map((row) => (
              <Box
                key={row._id}
                sx={{
                  px: 3,
                  py: 2,
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1.5fr 1.5fr 1.5fr 2fr 1.5fr",
                  gap: 2,
                  alignItems: "center",
                  borderTop: "1px solid #374151",
                  backgroundColor: "#273142",
                  "&:hover": { backgroundColor: "#323D4E" },
                }}
              >
                <Box>{row.deviceId}</Box>
                <Box>{row.connectivityStatus}</Box>
                <Box>{row.batteryStatus}</Box>
                <Box>{row.healthCenter}</Box>
                <Box>{row.location}</Box>
              </Box>
            ))
          )}
        </CardContent>
      </Card>

      {/* Add Device Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => !saving && setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#273142",
            borderRadius: 4,
            boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
            m: { xs: 2, sm: 4 },
          },
        }}
      >
        <DialogContent sx={{ p: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography variant="h5" sx={{ color: "#FFFFFF", fontWeight: 600 }}>
              Register New Device
            </Typography>
            <IconButton onClick={() => setOpenDialog(false)} disabled={saving} sx={{ color: "#91969E" }}>
              <Close fontSize="medium" />
            </IconButton>
          </Box>

          <Stack spacing={3.5}>
            <TextField
              fullWidth
              label="Device ID"
              placeholder="e.g., Mari-020"
              value={form.deviceId}
              onChange={(e) => setForm({ ...form, deviceId: e.target.value })}
              error={!!formErrors.deviceId}
              helperText={formErrors.deviceId}
              disabled={saving}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#3A4659",
                  color: "#E5E7EB",
                  "& fieldset": { borderColor: "#4B5563" },
                  "&:hover fieldset": { borderColor: "#6B7280" },
                  "&.Mui-focused fieldset": { borderColor: "#4880FF" },
                },
                "& .MuiInputLabel-root": { color: "#9CA3AF" },
                "& .MuiFormHelperText-root": { color: "#F87171" },
              }}
            />

            <TextField
              fullWidth
              label="Health Center"
              placeholder="e.g., All Saints Buchanangandi H/C III"
              value={form.healthCenter}
              onChange={(e) => setForm({ ...form, healthCenter: e.target.value })}
              error={!!formErrors.healthCenter}
              helperText={formErrors.healthCenter}
              disabled={saving}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#3A4659",
                  color: "#E5E7EB",
                  "& fieldset": { borderColor: "#4B5563" },
                  "&:hover fieldset": { borderColor: "#6B7280" },
                  "&.Mui-focused fieldset": { borderColor: "#4880FF" },
                },
                "& .MuiInputLabel-root": { color: "#9CA3AF" },
                "& .MuiFormHelperText-root": { color: "#F87171" },
              }}
            />

            <TextField
              fullWidth
              label="Location"
              placeholder="e.g., Kibuku, Uganda"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              error={!!formErrors.location}
              helperText={formErrors.location}
              disabled={saving}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#3A4659",
                  color: "#E5E7EB",
                  "& fieldset": { borderColor: "#4B5563" },
                  "&:hover fieldset": { borderColor: "#6B7280" },
                  "&.Mui-focused fieldset": { borderColor: "#4880FF" },
                },
                "& .MuiInputLabel-root": { color: "#9CA3AF" },
                "& .MuiFormHelperText-root": { color: "#F87171" },
              }}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleAddDevice}
                disabled={saving}
                sx={{
                  backgroundColor: "#4880FF",
                  color: "white",
                  px: 6,
                  py: 1.2,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: 140,
                  "&:hover": { backgroundColor: "#3A6BE0" },
                }}
              >
                {saving ? <CircularProgress size={24} color="inherit" /> : "Register Device"}
              </Button>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", maxWidth: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeviceRegistryPage;